import type { CorpusNode } from '../stores/corpus'
import { useSettingsStore } from '../stores/settings'

/**
 * Corpus search syntax. Plain text behaves exactly as it always has: the
 * words form one case-insensitive phrase matched anywhere. Everything else is
 * opt-in and only recognized in forms ordinary searches do not produce:
 *
 *   "red door"      whole-word phrase           -draft / -"first pass"  exclude
 *   in:title|alias|body  where to look          type:page|folder|desk
 *   desk:Name       inside a desk (any desk)    under:Name / path:Name  inside a folder
 *   scope:all       whole corpus, not just the current desk
 *   export:yes|no   wordcount:yes|no            has:alias|children|icon|color|text|synopsis
 *   status:Drafting (a page status by name)  status:none
 *   is:empty|open   words:>1000 / words:<200 / words:100..500
 *
 * Any filter can be negated with a leading "-" (e.g. -has:alias). A key the
 * parser does not know, or a known key with an invalid value, is searched as
 * literal text, so "Note: …" or "time:" never silently turn into a filter.
 */

export type SearchField = 'title' | 'alias' | 'body'

export interface SearchTerm {
  text: string
  /** Quoted: matched as whole words rather than anywhere inside a word. */
  exact: boolean
}

export interface SearchFilterContext {
  node: CorpusNode
  ancestors: CorpusNode[]
  /** Page text blocks, or undefined when not (yet) loaded. */
  blocks: string[] | undefined
  activePageId: string | null
}

export interface SearchFilter {
  /** Canonical token, shown back to the user so they can see it was understood. */
  label: string
  needsBody: boolean
  test: (context: SearchFilterContext) => boolean
}

export interface ParsedSearchQuery {
  terms: SearchTerm[]
  excluded: SearchTerm[]
  fields: Set<SearchField>
  filters: SearchFilter[]
  wholeCorpus: boolean
  /** True when the query contains anything to search or filter by. */
  isEmpty: boolean
}

const ALL_FIELDS: SearchField[] = ['title', 'alias', 'body']
const MIN_BODY_TERM_LENGTH = 2

const FIELD_NAMES: Record<string, SearchField> = {
  title: 'title', titles: 'title', name: 'title', names: 'title',
  alias: 'alias', aliases: 'alias',
  body: 'body', text: 'body', content: 'body', contents: 'body',
}

const TYPE_NAMES: Record<string, CorpusNode['subtype']> = {
  page: 'page', pages: 'page', doc: 'page', docs: 'page', document: 'page', documents: 'page',
  folder: 'folder', folders: 'folder',
  desk: 'desk', desks: 'desk',
}

const YES = new Set(['yes', 'y', 'true', 'on', 'included', 'include', 'in'])
const NO = new Set(['no', 'n', 'false', 'off', 'excluded', 'exclude', 'out'])

// Optional "-", optional "key:", then a quoted or bare value. Straight and
// curly quotes are both accepted; an unclosed quote falls through to the bare
// alternative and is searched literally.
const TOKEN = /(-)?(?:([A-Za-z]+):)?(?:"([^"]*)"|“([^”]*)”|(\S+))/gu

function fold(value: string): string {
  return value.normalize('NFKC').toLocaleLowerCase()
}

function includesFolded(haystack: string, needle: string): boolean {
  return fold(haystack).includes(fold(needle))
}

function parseYesNo(value: string): boolean | null {
  const folded = fold(value)
  if (YES.has(folded)) return true
  if (NO.has(folded)) return false
  return null
}

const wordCounts = new WeakMap<string[], number>()
export function countWords(blocks: string[]): number {
  let count = wordCounts.get(blocks)
  if (count === undefined) {
    count = 0
    for (const block of blocks) count += block.split(/\s+/).filter(Boolean).length
    wordCounts.set(blocks, count)
  }
  return count
}

function parseWordRange(value: string): { min: number; max: number } | null {
  let match = /^(>=|<=|>|<)(\d+)$/.exec(value)
  if (match) {
    const n = Number(match[2])
    switch (match[1]) {
      case '>': return { min: n + 1, max: Infinity }
      case '>=': return { min: n, max: Infinity }
      case '<': return { min: 0, max: n - 1 }
      default: return { min: 0, max: n }
    }
  }
  match = /^(\d+)(?:\.\.|-)(\d+)$/.exec(value)
  if (match) {
    const a = Number(match[1])
    const b = Number(match[2])
    return { min: Math.min(a, b), max: Math.max(a, b) }
  }
  if (/^\d+$/.test(value)) return { min: Number(value), max: Number(value) }
  return null
}

function hasBodyText(blocks: string[]): boolean {
  return blocks.some(block => block.trim().length > 0)
}

interface KeyResult {
  filter?: Omit<SearchFilter, 'label'>
  fields?: SearchField[]
  wholeCorpus?: boolean
}

/** Returns null when the value is not valid for the key. */
const KEYS: Record<string, (value: string) => KeyResult | null> = {
  in(value) {
    const fields = value.split(',').map(part => FIELD_NAMES[fold(part)])
    return fields.length && fields.every(Boolean) ? { fields } : null
  },
  type(value) {
    const types = value.split(',').map(part => TYPE_NAMES[fold(part)])
    if (!types.length || !types.every(Boolean)) return null
    return { filter: { needsBody: false, test: ({ node }) => types.includes(node.subtype) } }
  },
  desk(value) {
    return {
      wholeCorpus: true,
      filter: {
        needsBody: false,
        test: ({ ancestors }) => ancestors.some(a => a.subtype === 'desk' && includesFolded(a.name, value)),
      },
    }
  },
  under(value) {
    return {
      filter: { needsBody: false, test: ({ ancestors }) => ancestors.some(a => includesFolded(a.name, value)) },
    }
  },
  scope(value) {
    return ['all', 'corpus', 'everywhere', 'everything'].includes(fold(value)) ? { wholeCorpus: true } : null
  },
  export(value) {
    const wanted = parseYesNo(value)
    if (wanted === null) return null
    return { filter: { needsBody: false, test: ({ node }) => (node.includeInExport !== false) === wanted } }
  },
  wordcount(value) {
    const wanted = parseYesNo(value)
    if (wanted === null) return null
    return { filter: { needsBody: false, test: ({ node }) => (node.includeInWordCount !== false) === wanted } }
  },
  has(value) {
    switch (fold(value)) {
      case 'alias': case 'aliases':
        return { filter: { needsBody: false, test: ({ node }) => (node.aliases || []).some(a => a.trim()) } }
      case 'children': case 'child': case 'subpages':
        return { filter: { needsBody: false, test: ({ node }) => (node.children?.length || 0) > 0 } }
      case 'icon':
        return { filter: { needsBody: false, test: ({ node }) => Boolean(node.icon) } }
      case 'color': case 'colour':
        return { filter: { needsBody: false, test: ({ node }) => Boolean(node.color) } }
      case 'synopsis':
        return { filter: { needsBody: false, test: ({ node }) => Boolean(node.synopsis) } }
      case 'text': case 'content': case 'body':
        return {
          filter: {
            needsBody: true,
            test: ({ node, blocks }) => node.subtype === 'page' && Boolean(blocks && hasBodyText(blocks)),
          },
        }
      default:
        return null
    }
  },
  status(value) {
    const wanted = fold(value)
    if (['none', 'no', 'unset'].includes(wanted)) {
      return { filter: { needsBody: false, test: ({ node }) => node.subtype === 'page' && !node.status } }
    }
    const status = useSettingsStore().pageStatuses.find((s) => fold(s.label) === wanted || fold(s.id) === wanted)
    if (!status) return null
    return { filter: { needsBody: false, test: ({ node }) => node.subtype === 'page' && node.status === status.id } }
  },
  is(value) {
    switch (fold(value)) {
      case 'empty': case 'blank':
        return {
          filter: {
            needsBody: true,
            test: ({ node, blocks }) => node.subtype === 'page' && Boolean(blocks && !hasBodyText(blocks)),
          },
        }
      case 'open': case 'active': case 'current':
        return { filter: { needsBody: false, test: ({ node, activePageId }) => node.id === activePageId } }
      default:
        return null
    }
  },
  words(value) {
    const range = parseWordRange(value)
    if (!range) return null
    return {
      filter: {
        needsBody: true,
        test: ({ node, blocks }) => {
          if (node.subtype !== 'page' || !blocks) return false
          const count = countWords(blocks)
          return count >= range.min && count <= range.max
        },
      },
    }
  },
}
KEYS.path = KEYS.under
KEYS.folder = KEYS.under
KEYS.kind = KEYS.type
KEYS.wc = KEYS.wordcount

function quoteIfNeeded(value: string): string {
  return /\s/.test(value) ? `"${value}"` : value
}

export function parseSearchQuery(query: string): ParsedSearchQuery {
  const freeWords: string[] = []
  const terms: SearchTerm[] = []
  const excluded: SearchTerm[] = []
  const filters: SearchFilter[] = []
  const includeFields = new Set<SearchField>()
  const excludeFields = new Set<SearchField>()
  let wholeCorpus = false

  TOKEN.lastIndex = 0
  let match: RegExpExecArray | null
  while ((match = TOKEN.exec(query))) {
    const [raw, negation, rawKey, straightQuoted, curlyQuoted, bare] = match
    const quoted = straightQuoted ?? curlyQuoted
    const value = (quoted ?? bare ?? '').trim()
    const negated = Boolean(negation)

    if (rawKey) {
      const key = rawKey.toLowerCase()
      const handler = Object.prototype.hasOwnProperty.call(KEYS, key) ? KEYS[key] : undefined
      const result = value && handler ? handler(value) : null
      if (result) {
        if (result.fields) result.fields.forEach(field => (negated ? excludeFields : includeFields).add(field))
        if (result.wholeCorpus && !negated) wholeCorpus = true
        if (result.filter) {
          const { test } = result.filter
          filters.push({
            label: `${negated ? '-' : ''}${key}:${quoteIfNeeded(value)}`,
            needsBody: result.filter.needsBody,
            test: negated ? context => !test(context) : test,
          })
        }
        continue
      }
      // Not a filter after all: search the token as it was typed.
      freeWords.push(raw)
      continue
    }

    if (quoted !== undefined) {
      if (value) (negated ? excluded : terms).push({ text: value, exact: true })
    } else if (negated && value) {
      excluded.push({ text: value, exact: false })
    } else if (raw) {
      freeWords.push(raw)
    }
  }

  if (freeWords.length) terms.unshift({ text: freeWords.join(' '), exact: false })

  const fields = new Set<SearchField>(includeFields.size ? includeFields : ALL_FIELDS)
  excludeFields.forEach(field => fields.delete(field))

  return {
    terms,
    excluded,
    fields,
    filters,
    wholeCorpus,
    isEmpty: !terms.length && !excluded.length && !filters.length,
  }
}

/** Short unquoted terms would match nearly every document's text. */
export function isBodySearchable(term: SearchTerm): boolean {
  return term.exact || term.text.length >= MIN_BODY_TERM_LENGTH
}

export function queryNeedsBody(parsed: ParsedSearchQuery): boolean {
  if (parsed.filters.some(filter => filter.needsBody)) return true
  if (!parsed.fields.has('body')) return false
  return [...parsed.terms, ...parsed.excluded].some(isBodySearchable)
}

function isWordCharacter(value: string | undefined): boolean {
  return Boolean(value && /[\p{L}\p{N}_]/u.test(value))
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function termPattern(term: SearchTerm): string {
  const tokens = term.text.trim().split(/\s+/).filter(Boolean)
  let pattern = tokens.map(escapeRegExp).join('\\s+')
  if (term.exact) {
    // Only enforce a boundary on an edge that is itself a word character, so
    // "Mr." still matches before a space and "—" matches between words.
    const text = term.text.trim()
    if (isWordCharacter(text[0])) pattern = `(?<![\\p{L}\\p{N}_])${pattern}`
    if (isWordCharacter(text[text.length - 1])) pattern = `${pattern}(?![\\p{L}\\p{N}_])`
  }
  return pattern
}

/**
 * Case-insensitive matcher for one or more terms. Longer terms come first so
 * an alternation prefers "red door" over "red" at the same position.
 */
export function createTermsMatcher(terms: SearchTerm[]): RegExp | null {
  const patterns = terms
    .filter(term => term.text.trim())
    .sort((a, b) => b.text.length - a.text.length)
    .map(termPattern)
  if (!patterns.length) return null
  try {
    return new RegExp(patterns.join('|'), 'giu')
  } catch {
    return null
  }
}

export function describeFilters(parsed: ParsedSearchQuery): string[] {
  const labels: string[] = []
  if (parsed.fields.size < ALL_FIELDS.length) labels.push(`in:${[...parsed.fields].join(',')}`)
  if (parsed.wholeCorpus && !parsed.filters.some(f => f.label.startsWith('desk:'))) labels.push('scope:all')
  labels.push(...parsed.filters.map(filter => filter.label))
  labels.push(...parsed.excluded.map(term => `-${term.exact ? `"${term.text}"` : term.text}`))
  return labels
}
